import { useEffect, createContext, useContext } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth, SignIn, SignUp } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { setGetTokenFn, getMe } from "./lib/api";

export const PlanContext = createContext<string>("free");
export function usePlan() { return useContext(PlanContext); }
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import Stars from "./components/Stars";

// Pages
import Home from "./pages/Home";
import ShareResult from "./pages/ShareResult";
import Dashboard from "./pages/Dashboard";
import Monitors from "./pages/Monitors";
import MonitorDetail from "./pages/MonitorDetail";
import MonitorGroupDetail from "./pages/MonitorGroupDetail";
import StatusPages from "./pages/StatusPages";
import StatusPageEditor from "./pages/StatusPageEditor";
import StatusPagePublic from "./pages/StatusPagePublic";
import History from "./pages/History";
import Upgrade from "./pages/Upgrade";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

const clerkAppearance = {
  variables: {
    colorPrimary: "#FF2566",
    colorBackground: "#121212",
    colorText: "#EFF2F5",
    colorTextSecondary: "#98A1AB",
    colorInputBackground: "rgba(18, 18, 18, 0.9)",
    colorInputText: "#EFF2F5",
    borderRadius: "3px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif",
  },
  elements: {
    card: {
      background: "rgba(18, 18, 18, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
    },
    headerTitle: { color: "#EFF2F5" },
    headerSubtitle: { color: "#98A1AB" },
    socialButtonsBlockButton: {
      background: "rgba(18, 18, 18, 0.8)",
      border: "1px solid rgba(255, 255, 255, 0.15)",
      color: "#EFF2F5",
    },
    dividerLine: { background: "rgba(255, 255, 255, 0.08)" },
    dividerText: { color: "#98A1AB" },
    formFieldLabel: { color: "#98A1AB" },
    footerActionLink: { color: "#FF2566" },
    identityPreviewText: { color: "#EFF2F5" },
    identityPreviewEditButton: { color: "#FF2566" },
  },
};

function AuthPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px" }}>
      <Stars />
      <div style={{ position: "relative", zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

function Layout() {
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Stars />
      <Nav />
      <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}


export default function App() {
  const { getToken, isSignedIn } = useAuth();

  // Set synchronously during render so token is available before any query fires.
  // useEffect runs after paint — too late for queries that mount in the same pass.
  setGetTokenFn(isSignedIn ? getToken : null);

  // Clean up on unmount.
  useEffect(() => () => setGetTokenFn(null), []);

  // Fetch plan from DB (also ensures the DB row exists for this Clerk user).
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: !!isSignedIn,
    staleTime: 5 * 60 * 1000,
  });
  const plan = me?.plan ?? "free";

  return (
    <PlanContext.Provider value={plan}>
    <ErrorBoundary>
    <Routes>
      {/* Sign-in/sign-up redirect to dashboard if already authenticated */}
      <Route path="/sign-in/*" element={isSignedIn ? <Navigate to="/dashboard" replace /> : <AuthPageWrapper><SignIn routing="path" path="/sign-in" appearance={clerkAppearance} /></AuthPageWrapper>} />
      <Route path="/sign-up/*" element={isSignedIn ? <Navigate to="/dashboard" replace /> : <AuthPageWrapper><SignUp routing="path" path="/sign-up" appearance={clerkAppearance} /></AuthPageWrapper>} />

      {/* Public status page — no nav/footer chrome */}
      <Route path="/status/:slug" element={<StatusPagePublic />} />

      {/* All other routes share the persistent Nav layout */}
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/r/:runId" element={<ShareResult />} />
        <Route path="/upgrade" element={<Pricing />} />
        <Route path="/checkout" element={<Upgrade />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/dashboard/history" element={<RequireAuth><History /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />

        <Route path="/dashboard/monitors" element={<RequireAuth><Monitors /></RequireAuth>} />
        <Route path="/dashboard/monitors/:id" element={<RequireAuth><MonitorDetail /></RequireAuth>} />
        <Route path="/dashboard/monitor-groups/:id" element={<RequireAuth><MonitorGroupDetail /></RequireAuth>} />

        <Route path="/dashboard/status-pages" element={<RequireAuth><StatusPages /></RequireAuth>} />
        <Route path="/dashboard/status-pages/:id" element={<RequireAuth><StatusPageEditor /></RequireAuth>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </ErrorBoundary>
    </PlanContext.Provider>
  );
}
